import { useState } from "react";
import { CheckCircle2, Hammer, Package, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const STAGES = [
  { id: "placed", label: "Order Placed", icon: Package },
  { id: "processing", label: "Crafting & Inspection", icon: Hammer },
  { id: "shipped", label: "Dispatched via Courier", icon: Truck },
  { id: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const stageIndex = (status) =>
  ({ awaiting_payment: 0, processing: 1, shipped: 2, delivered: 3, cancelled: -1 }[status] ?? 0);

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3.5 font-mono text-xs tracking-wider text-bone outline-none transition-colors placeholder:text-faint focus:border-vermilion";

export default function Track() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`);
      setOrder(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const current = order ? stageIndex(order.status) : -1;

  return (
    <div className="px-6 pb-24 pt-36 sm:px-12 lg:px-20" data-testid="track-page">
      <Reveal>
        <p className="eyebrow-label mb-4">Order Tracking</p>
        <h1 className="max-w-2xl font-display text-4xl font-light leading-tight text-bone sm:text-5xl lg:text-6xl">
          Where is your object now?
        </h1>
      </Reveal>

      <Reveal delay={0.1}>
        <form onSubmit={submit} className="mt-12 grid max-w-3xl gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <input required placeholder="ORDER CODE — AN-1001" value={code} onChange={(e) => setCode(e.target.value)} data-testid="track-order-input" className={inputCls} />
          <input required type="email" placeholder="EMAIL USED AT CHECKOUT" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-email-input" className={inputCls} />
          <button type="submit" disabled={loading} data-testid="track-order-submit" className="bg-vermilion px-8 py-3.5 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:opacity-40">
            {loading ? "…" : "Trace"}
          </button>
        </form>
        <p className="mt-3 font-mono text-[10px] tracking-wider text-faint">Try the demo order — code AN-1001, email demo@ateliernorte.com</p>
      </Reveal>

      {order && (
        <Reveal className="mt-16">
          <div className="border border-white/10 bg-onyx p-8 lg:p-12" data-testid="tracking-result">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint">Order</p>
                <p className="mt-1 font-display text-4xl text-bone" data-testid="tracked-order-code">{order.code}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint">Total</p>
                <p className="mt-1 font-mono text-xl text-bone">{money(order.total)}</p>
              </div>
            </div>

            {order.status === "cancelled" ? (
              <p className="mt-10 border border-destructive/40 bg-destructive/10 px-5 py-4 font-mono text-xs uppercase tracking-[0.25em] text-red-400">
                This order was cancelled. Write to the concierge for details.
              </p>
            ) : (
              <div className="mt-12 grid gap-8 sm:grid-cols-4" data-testid="tracking-timeline">
                {STAGES.map((s, i) => {
                  const reached = i <= current;
                  const Icon = s.icon;
                  return (
                    <div key={s.id} className="relative">
                      {i < STAGES.length - 1 && (
                        <div className={`absolute left-7 top-6 hidden h-px w-[calc(100%-2rem)] sm:block ${i < current ? "bg-vermilion" : "bg-white/10"}`} />
                      )}
                      <div className={`relative z-10 flex h-12 w-12 items-center justify-center border ${reached ? "border-vermilion bg-vermilion/10 text-vermilion" : "border-white/15 text-faint"}`}>
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <p className={`mt-4 font-mono text-[10px] uppercase tracking-[0.25em] ${reached ? "text-bone" : "text-faint"}`}>{s.label}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-12 grid gap-10 border-t border-white/10 pt-8 md:grid-cols-2">
              <div>
                <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-faint">Objects in this order</p>
                {order.items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-4 py-2">
                    {it.image && <img src={it.image} alt={it.name} className="h-12 w-10 border border-white/10 object-cover" />}
                    <span className="flex-1 font-light text-bone">{it.name} <span className="text-faint">× {it.quantity}</span></span>
                    <span className="font-mono text-xs text-smoke">{money(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="font-mono text-xs leading-loose tracking-wider text-smoke">
                <p className="mb-4 uppercase tracking-[0.3em] text-faint">Destination</p>
                <p>{order.name}</p>
                <p>{[order.address, order.city, order.country].filter(Boolean).join(", ") || "Address confirmed by courier"}</p>
                <p className="mt-4">{order.email}</p>
              </div>
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}
